import React, {useEffect, useState} from 'react';
import {View, ScrollView, StyleSheet, Image} from 'react-native';
import {
  Text,
  Appbar,
  ActivityIndicator,
  Chip,
  Divider,
  Button,
  Snackbar,
} from 'react-native-paper';
import {useNavigation, useRoute} from '@react-navigation/native';
import {useTranslation} from 'react-i18next';
import {colors, spacing} from '../../theme';
import {orgUnitApi} from '../../services/api/orgUnitApi';
import {subscriptionApi} from '../../services/api/subscriptionApi';

const ORG_TYPES = {
  OUTLET: 'Outlet',
  FRANCHISE: 'Franchise',
  MASTER_FRANCHISE: 'Master Franchise',
  WAREHOUSE: 'Warehouse',
  OWNER_GROUP: 'Owner Group',
};

const formatDate = dateStr => {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  if (isNaN(d)) return dateStr;
  return d.toLocaleDateString('en-IN', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
};

const getStatusColor = status => {
  switch (status) {
    case 'ACTIVE':
      return colors.success;
    case 'TRIAL':
      return colors.info;
    case 'EXPIRED':
    case 'SUSPENDED':
      return colors.error;
    default:
      return colors.textSecondary;
  }
};

const StoreViewScreen = () => {
  const {t} = useTranslation();
  const navigation = useNavigation();
  const route = useRoute();
  const {storeId} = route.params;

  const [store, setStore] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [storeRes, invoiceRes] = await Promise.all([
          orgUnitApi.getById(storeId),
          subscriptionApi.listInvoices(),
        ]);
        setStore(storeRes.data?.data || storeRes.data);
        const allInvoices = invoiceRes.data?.data || invoiceRes.data || [];
        // Filter invoices that have lines matching this org unit
        const filtered = allInvoices.filter(inv =>
          (inv.lines || []).some(line => line.orgUnitId === storeId),
        );
        setInvoices(filtered);
      } catch (e) {
        setSnackbar(e.response?.data?.error?.message || t('common.error'));
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [storeId, t]);

  if (loading) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color={colors.primary} />
      </View>
    );
  }

  if (!store) {
    return (
      <View style={styles.container}>
        <Appbar.Header style={styles.appbar} dark>
          <Appbar.BackAction
            onPress={() => navigation.goBack()}
            color={colors.white}
          />
          <Appbar.Content title="Store" titleStyle={styles.appbarTitle} />
        </Appbar.Header>
        <View style={styles.loadingContainer}>
          <Text style={{color: colors.textSecondary}}>
            {t('common.noData')}
          </Text>
        </View>
      </View>
    );
  }

  const isExpiredOrTrial =
    store.subscriptionStatus === 'EXPIRED' ||
    store.subscriptionStatus === 'SUSPENDED' ||
    store.subscriptionStatus === 'TRIAL';

  return (
    <View style={styles.container}>
      <Appbar.Header style={styles.appbar} dark>
        <Appbar.BackAction
          onPress={() => navigation.goBack()}
          color={colors.white}
        />
        <Appbar.Content
          title={store.name}
          titleStyle={styles.appbarTitle}
        />
      </Appbar.Header>

      <ScrollView style={styles.content}>
        {/* Store Header */}
        <View style={styles.header}>
          <View style={styles.headerTop}>
            {store.logoUrl || store.photoUrl ? (
              <Image
                source={{uri: store.logoUrl || store.photoUrl}}
                style={styles.headerImage}
                resizeMode="cover"
              />
            ) : (
              <View style={styles.headerImagePlaceholder}>
                <Text style={styles.headerImageText}>
                  {store.name?.charAt(0)?.toUpperCase() || 'S'}
                </Text>
              </View>
            )}
            <View style={styles.headerInfo}>
              <Text style={styles.headerName}>{store.name}</Text>
              {store.code && (
                <Text style={styles.headerCode}>Code: {store.code}</Text>
              )}
              <View style={styles.chipRow}>
                {store.orgType && (
                  <Chip style={styles.typeChip} textStyle={styles.chipText} compact>
                    <Text style={{fontSize: 13, color: '#000', marginTop:0}}>{ORG_TYPES[store.orgType] || store.orgType}</Text>
                  </Chip>
                )}
                {store.status && (
                  <Chip
                    style={[
                      styles.statusChip,
                      {
                        backgroundColor:
                          store.status === 'ACTIVE'
                            ? colors.success + '20'
                            : colors.textSecondary + '20',
                      },
                    ]}
                    textStyle={[
                      styles.chipText,
                      {
                        color:
                          store.status === 'ACTIVE'
                            ? colors.success
                            : colors.textSecondary,
                      },
                    ]}
                    compact>
                    <Text style={{fontSize: 10, color: '#000', marginTop:2}}>{store.status}</Text>
                  </Chip>
                )}
              </View>
            </View>
          </View>
        </View>

        <Divider style={styles.divider} />

        {/* Contact Information */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Contact Information</Text>
          <Row label="Phone" value={store.phone} />
          <Row label="WhatsApp" value={store.whatsappPhone} />
          <Row label="Email" value={store.email} />
          <Row label="Instagram" value={store.instagram} />
          <Row label="Website" value={store.website} />
        </View>

        <Divider style={styles.divider} />

        {/* Subscription */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Subscription</Text>
          <Row
            label="Plan"
            value={store.planName || '—'}
            valueColor={getStatusColor(store.subscriptionStatus)}
          />
          <View style={styles.row}>
            <Text style={styles.rowLabel}>Status</Text>
            {store.subscriptionStatus ? (
              <Chip
                style={[
                  styles.statusChipSmall,
                  {backgroundColor: getStatusColor(store.subscriptionStatus) + '20'},
                ]}
                textStyle={[
                  styles.chipTextSmall,
                  {color: getStatusColor(store.subscriptionStatus)},
                ]}
                compact>
                <Text style={{fontSize: 11, color: '#000', marginTop:2}}>{store.subscriptionStatus}</Text>
              </Chip>
            ) : (
              <Text style={styles.rowValue}>—</Text>
            )}
          </View>
          <Row label="Expires" value={formatDate(store.planExpiryDate)} />
          {isExpiredOrTrial && (
            <Button
              mode="contained"
              style={styles.renewButton}
              labelStyle={styles.renewButtonLabel}
              onPress={() =>
                navigation.navigate('PlanSelection', {
                  orgUnitId: store.id,
                  orgUnitName: store.name,
                  subscriptionId: store.subscriptionId,
                })
              }>
              {store.subscriptionStatus === 'TRIAL' ? 'Upgrade' : 'Renew'}
            </Button>
          )}
        </View>

        <Divider style={styles.divider} />

        {/* Address */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Address</Text>
          <Row label="Address Line 1" value={store.addressLine1} />
          {store.addressLine2 ? (
            <Row label="Address Line 2" value={store.addressLine2} />
          ) : null}
          <Row label="Area" value={store.area} />
          <Row label="City" value={store.city} />
          <Row label="State" value={store.state} />
          <Row label="Country" value={store.country} />
        </View>

        <Divider style={styles.divider} />

        {/* Payment History */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Payment History</Text>
          {invoices.length > 0 ? (
            <View style={styles.table}>
              <View style={styles.tableHeaderRow}>
                <Text style={[styles.tableCell, {flex: 2}]}>Invoice #</Text>
                <Text style={[styles.tableCell, {flex: 2}]}>Date</Text>
                <Text style={[styles.tableCell, {flex: 1, textAlign: 'right'}]}>
                  Amount
                </Text>
                <Text style={[styles.tableCell, {flex: 1.5, textAlign: 'right'}]}>
                  Status
                </Text>
              </View>
              {invoices.map((inv, idx) => (
                <View
                  key={inv.id}
                  style={[
                    styles.tableRow,
                    idx % 2 === 0 && styles.tableRowAlt,
                  ]}>
                  <Text style={[styles.tableCell, {flex: 2}]}>
                    {inv.invoiceNumber || inv.id}
                  </Text>
                  <Text style={[styles.tableCell, {flex: 2}]}>
                    {formatDate(inv.invoiceDate || inv.createdAt)}
                  </Text>
                  <Text style={[styles.tableCell, {flex: 1, textAlign: 'right'}]}>
                    {'\u20B9'}
                    {Number(inv.totalAmount || 0).toLocaleString()}
                  </Text>
                  <Text
                    style={[
                      styles.tableCell,
                      {
                        flex: 1.5,
                        textAlign: 'right',
                        color:
                          inv.status === 'PAID' ? colors.success : colors.error,
                      },
                    ]}>
                    {inv.status || '—'}
                  </Text>
                </View>
              ))}
            </View>
          ) : (
            <Text style={styles.emptyText}>No invoices yet</Text>
          )}
        </View>

        <View style={{height: spacing.xxl}} />
      </ScrollView>

      <Snackbar
        visible={!!snackbar}
        onDismiss={() => setSnackbar('')}
        duration={3000}>
        {snackbar}
      </Snackbar>
    </View>
  );
};

const Row = ({label, value, valueColor}) => (
  <View style={styles.row}>
    <Text style={styles.rowLabel}>{label}</Text>
    <Text style={[styles.rowValue, valueColor && {color: valueColor}]}>
      {value || '—'}
    </Text>
  </View>
);

const styles = StyleSheet.create({
  container: {flex: 1, backgroundColor: colors.background},
  appbar: {backgroundColor: colors.primary},
  appbarTitle: {color: colors.white},
  content: {flex: 1},
  loadingContainer: {flex: 1, justifyContent: 'center', alignItems: 'center'},

  // Header
  header: {
    backgroundColor: '#EEEEEE',
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.md,
  },
  headerTop: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  headerImage: {
    width: 80,
    height: 80,
    borderRadius: 10,
  },
  headerImagePlaceholder: {
    width: 80,
    height: 80,
    borderRadius: 10,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: colors.primary + '15',
  },
  headerImageText: {
    fontSize: 32,
    fontWeight: '700',
    color: colors.primary,
  },
  headerInfo: {
    flex: 1,
    marginLeft: spacing.md,
  },
  headerName: {
    fontSize: 20,
    fontWeight: '700',
    color: colors.text,
  },
  headerCode: {
    fontSize: 14,
    color: colors.textSecondary,
    marginTop: 2,
  },
  chipRow: {
    flexDirection: 'row',
    gap: 8,
    marginTop: spacing.xs,
  },
  typeChip: {
    backgroundColor: colors.textSecondary + '18',
    height: 26,
  },
  statusChip: {
    height: 26,
  },
  chipText: {
    fontSize: 11,
  },

  // Sections
  section: {paddingHorizontal: spacing.md, paddingVertical: spacing.sm},
  sectionTitle: {
    fontSize: 16,
    fontWeight: '700',
    color: colors.text,
    marginBottom: spacing.sm,
  },
  divider: {marginHorizontal: spacing.md},

  // Rows
  row: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: spacing.xs,
  },
  rowLabel: {fontSize: 14, color: colors.textSecondary},
  rowValue: {fontSize: 14, fontWeight: '500', color: colors.text},

  // Subscription
  statusChipSmall: {height: 24},
  chipTextSmall: {fontSize: 11, fontWeight: '600'},
  renewButton: {
    marginTop: spacing.sm,
    backgroundColor: colors.primary,
  },
  renewButtonLabel: {fontSize: 14},

  // Table
  table: {
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: 4,
    overflow: 'hidden',
  },
  tableHeaderRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: spacing.sm,
    paddingHorizontal: spacing.md,
    backgroundColor: '#E8E8E8',
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
  },
  tableRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: spacing.sm,
    paddingHorizontal: spacing.md,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
  },
  tableRowAlt: {backgroundColor: '#FAFAFA'},
  tableCell: {fontSize: 13, color: colors.text},

  emptyText: {
    textAlign: 'center',
    color: colors.textSecondary,
    paddingVertical: spacing.md,
  },
});

export default StoreViewScreen;
